/**
 * Hook for managing session refresh and validation
 * Automatically extends sessions and detects stale sessions
 */

import { useEffect, useRef } from 'react';
import { getAuthInstance } from '@/lib/firebase';
import { 
  updateSessionActivity, 
  extendSession, 
  isSessionStale, 
  hasValidSession,
  clearSession 
} from '@/lib/sessionStore';

const SESSION_REFRESH_INTERVAL = 5 * 60 * 1000; // Refresh every 5 minutes
const MAX_INACTIVITY_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Hook that manages session refresh and validation
 * - Periodically extends the session
 * - Clears stale sessions
 * - Syncs with Firebase auth state
 */
export function useSessionRefresh() {
  const refreshIntervalRef = useRef<NodeJS.Timeout>();
  const staleCheckIntervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    let isMounted = true;

    const setupSessionRefresh = async () => {
      try {
        const auth = await getAuthInstance();
        if (!auth) return;

        // Refresh session periodically
        refreshIntervalRef.current = setInterval(() => {
          if (isMounted && hasValidSession()) {
            extendSession(7 * 24 * 60 * 60 * 1000); // Extend by 7 days on each refresh
            console.debug('[sessionRefresh] Session extended');
          }
        }, SESSION_REFRESH_INTERVAL);

        // Check for stale sessions
        staleCheckIntervalRef.current = setInterval(() => {
          if (isMounted) {
            if (isSessionStale(MAX_INACTIVITY_DURATION)) {
              console.warn('[sessionRefresh] Session is stale, clearing');
              clearSession();
            }
          }
        }, 60 * 60 * 1000); // Check every hour
      } catch (err) {
        console.warn('[sessionRefresh] Failed to setup session refresh', err);
      }
    };

    setupSessionRefresh();

    return () => {
      isMounted = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
      }
    };
  }, []);
}

/**
 * Hook to sync session activity across tabs
 */
export function useSessionActivitySync() {
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible, update activity
        updateSessionActivity();
        console.debug('[sessionActivitySync] Activity updated on tab focus');
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      // Sync session state from other tabs
      if (event.key?.startsWith('app:session')) {
        console.debug('[sessionActivitySync] Session synced from other tab');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
}
